import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CancelUpcomingPaymentError, requestCancelUpcomingPayment, type UpcomingPayment } from './upcomingPaymentsApi';
import { upcomingPaymentsKey } from './useUpcomingPayments';

interface UseCancelUpcomingPaymentOptions {
  onSuccess?: () => void;
}

/**
 * Cancels one scheduled payment from the Upcoming payments table.
 *
 * Deliberately raises no toast: the dialog stays open on failure and shows the error inline, so a
 * toast would duplicate it. The caller reads `isError` and relabels its confirm button to retry.
 *
 * On success the cancelled row is pruned from the cache before the refetch, so the table updates
 * the moment the dialog closes rather than after the network round-trip, then invalidates so the
 * pruned list is reconciled with the server.
 *
 * Takes `onSuccess` as an option rather than a `submit` argument, matching `useRemoveCreditCard`
 * and `useRemoveBankAccount`, the two hooks behind the sibling confirm dialogs.
 */
export function useCancelUpcomingPayment({ onSuccess }: UseCancelUpcomingPaymentOptions = {}) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: requestCancelUpcomingPayment,
    onSuccess: (cancelledId: string) => {
      queryClient.setQueryData<UpcomingPayment[]>(upcomingPaymentsKey, (current) =>
        current?.filter((payment) => payment.id !== cancelledId),
      );
      void queryClient.invalidateQueries({ queryKey: upcomingPaymentsKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        const code = error instanceof CancelUpcomingPaymentError ? error.code : extractGqlErrorCode(error);
        trackException(error, { source: 'cancelUpcomingPayment', errorCode: code });
      }
    },
  });

  return {
    submit: (id: string) => mutation.mutate(id),
    isPending: mutation.isPending,
    isError: mutation.isError,
    /** Clears stale pending/error state — call when the dialog opens for a different payment id. */
    reset: mutation.reset,
  };
}
